import * as moment from 'moment';
import i18n from '@/plugins/i18n';
import { Business } from '@/models/businesses/business';
import { Category } from '@/models/references-classification/category';
import { Characteristic } from '@/models/references-classification/characteristic';
import { ReferenceImage } from '@/models/references/reference-image';
import { ReferenceSku } from '@/models/references/reference-sku';
import { ReferenceType } from '@/models/references/reference-type';
import { UrlPartInfo } from '@/models/common/url-part-info';
import { UserEntityPermissionsType } from '@/models/users/user-permissions/user-entity-permissions';

export class Reference {
    public id: number = 0;
    public type: ReferenceType = ReferenceType.Product;
    public projectId: number = 0;
    public associationId?: number;
    public businessId?: number;
    public businessPricingLevel?: number;
    public createdUtc?: moment.Moment;
    public businessTradeName?: string;
    public images: ReferenceImage[] = [];
    public title?: string;
    public description?: string;
    public category!: Category;
    public isNonPhysical: boolean = false;
    public nonPhysicalDeliveryInstructions?: string;
    public characteristics: Characteristic[] = [];
    public skus: ReferenceSku[] = [];
    public imagesCount: number = 0;
    public isPublishedInAConsumer: boolean = false;
    public publicCanonicalUrls: UrlPartInfo[] = [];
    public available: boolean = false;
    public isTimeSensitive: boolean = false;
    public hasBusinessAnActiveSalesAdmissionHoursSchedule: boolean = false;
    public hasBusinessAttributes: boolean = false;
    public particularConditions?: string;

    get created(): moment.Moment | undefined {
        return this.createdUtc ? this.createdUtc.local() : undefined;
    }

    get business(): Business {
        return this.businessId ? new Business({ id: this.businessId, tradeName: this.businessTradeName }) : new Business();
    }

    get displayTitle(): string {
        return this.title ? this.title : i18n.t('common.undefined').toString();
    }

    get principalSku(): ReferenceSku | undefined {
        return this.skus.find(sku => sku.isPrincipal);
    }

    get formattedPrincipalSkuPrice(): string | undefined {
        if (this.principalSku) {
            let result: string = '';

            if (this.principalSku.price != null) {
                if (this.principalSku.discountedPrice != null) {
                    result += `<small class="old-price mr-1"><del>${i18n.n(this.principalSku.price, 'currency')}</del></small>`;
                } else {
                    result += `<span class="current-price">${i18n.n(this.principalSku.price, 'currency')}</span>`;
                }
            }
            if (this.principalSku.discountedPrice != null) {
                result += `<span class="discounted-price">${i18n.n(this.principalSku.discountedPrice, 'currency')}</span>`;
            }
            result = '<span class="price">' + result + '</span>';
            if (this.principalSku.priceDetails) {
                result = `${result}<br/><small class="price-details">${this.principalSku.priceDetails.trim()}</small>`;
            }

            return result;
        }
        return undefined;
    }

    get allSkuPricesAreEqual(): boolean {
        if (this.principalSku === undefined) {
            return false;
        }
        return this.skus.map(sku => sku.price).every((price: number | null) => price === this.skus[0].price);
    }

    get permissionsGroupType(): UserEntityPermissionsType {
        return UserEntityPermissionsType.References;
    }

    get permissionsIdToCheck(): number {
        return this.id;
    }

    get formattedStockLevel(): string | undefined {
        let numberOfUndefinedStocks: number = 0;
        let result: string = '';
        this.skus.forEach(sku => {
            if (sku.availableUnits == null) {
                numberOfUndefinedStocks++;
            } else if (sku.availableUnits === 0) {
                result += '<span class="badge danger-color">' + sku.availableUnits + '</span> ';
            } else if (sku.availableUnits <= 3) {
                result += '<span class="badge warning-color">' + sku.availableUnits + '</span> ';
            } else {
                result += '<span class="badge primary-color">' + sku.availableUnits + '</span> ';
            }
        });
        if (this.skus.length < 1) {
            result = '';
        } else if (this.skus.length === numberOfUndefinedStocks) {
            result = '<span class="badge default-color"><i class="fas fa-infinity"></i></span>';
        }
        return '<div class="stock text-center"> ' + result + '</div>';
    }

    get publicCanonicalUrl(): string | undefined {
        return this.publicCanonicalUrls && this.publicCanonicalUrls.length > 0 ? this.publicCanonicalUrls.find(url => url.culture === i18n.locale)!.urlForEntitiesWithSlug : undefined;
    }

    get hasBusinessFunctionalLevelOver1(): boolean {
        return this.businessPricingLevel! > 1;
    }

    get hasBusinessFunctionalLevelOver2(): boolean {
        return this.businessPricingLevel! > 2;
    }

    get hasBusinessFunctionalLevelOver3(): boolean {
        return this.businessPricingLevel! > 3;
    }

    constructor(json?: any) {
        if (!json) {
            return;
        }

        Object.assign(this, json, {
            createdUtc: moment.utc(json.createdUtc),
            images: json.images ? json.images.map((image: any) => new ReferenceImage(image)) : [],
            category: json.category ? new Category(json.category) : new Category(),
            characteristics: json.characteristics ? json.characteristics.map((characteristic: any) => new Characteristic(characteristic)) : [],
            skus: json.skus ? json.skus.map((sku: any) => new ReferenceSku(sku)) : [],
            publicCanonicalUrls: json.publicCanonicalUrls ? json.publicCanonicalUrls.map((url: any) => new UrlPartInfo(url)) : []
        });

        if (this.description) {
            const Entities = require('html-entities').AllHtmlEntities;
            this.description = new Entities().decode(this.description);
        }

        if (this.particularConditions) {
            const Entities = require('html-entities').AllHtmlEntities;
            this.particularConditions = new Entities().decode(this.particularConditions);
        }
    }

    public clone(): Reference {
        const reference = new Reference();

        reference.id = this.id;
        reference.type = this.type;
        reference.projectId = this.projectId;
        reference.associationId = this.associationId;
        reference.businessId = this.businessId;
        reference.createdUtc = this.createdUtc;
        reference.businessTradeName = this.businessTradeName;
        reference.images.push(...this.images.map(image => image.clone()));
        reference.title = this.title;
        reference.description = this.description;
        reference.category = this.category.clone();
        reference.isNonPhysical = this.isNonPhysical;
        reference.nonPhysicalDeliveryInstructions = this.nonPhysicalDeliveryInstructions;
        reference.characteristics.push(...this.characteristics.map(characteristic => characteristic.clone()));
        reference.skus.push(...this.skus.map(sku => sku.clone()));
        reference.imagesCount = this.imagesCount;
        reference.isPublishedInAConsumer = this.isPublishedInAConsumer;
        reference.available = this.available;
        reference.isTimeSensitive = this.isTimeSensitive;
        reference.hasBusinessAnActiveSalesAdmissionHoursSchedule = this.hasBusinessAnActiveSalesAdmissionHoursSchedule;
        reference.hasBusinessAttributes = this.hasBusinessAttributes;
        reference.publicCanonicalUrls.push(...this.publicCanonicalUrls.map(publicCanonicalUrl => publicCanonicalUrl.clone()));
        reference.particularConditions = this.particularConditions;

        return reference;
    }
}
